import type { FilterTree } from "@/components/layout-2/filter-tree/types";
import type { Filter } from "@/components/layout/types";
import type { FieldDataType } from "@/types/dynamic-detail";
import { describeTree, diffFilters, toInstances } from "./filter-chips";
import type { DRQ, FeedingObject, FeedingPage } from "./types";

/**
 * Each step dialog of the Feeding tab edits a local draft of its own (settings, or ListViews) and saves
 * it on its own: the body carries only what the step changes, through the one Save endpoint.
 */

/** A ListView the Save will attach, with what the page needs to show it meanwhile. */
export type NewView = {
    key: string;
    name: string;
    object: FeedingObject;
    records: number | null;
    spec: { mode: "duplicate"; source_list_view_id: number } | { mode: "create"; name: string };
};

export type ListViewDraft = {
    /** Replaces the source. */
    source: NewView | null;
    /** New filters of the current source. */
    filters: { filters: DRQ; filter_logic: string | null; filter_tree?: FilterTree | null } | null;
    added: NewView[];
    /** Link ids of the exclusions to remove. */
    removed: number[];
};

export const NO_DRAFT: ListViewDraft = { source: null, filters: null, added: [], removed: [] };

/** One pending change: `label` for the save bar, `before` / `after` for the impact window. */
export type DraftChange = { kind: "added" | "removed" | "changed"; label: string; before?: string; after?: string };

const shown = (field: FieldDataType | undefined, value: unknown) => {
    const option = field?.options?.find(item => String(item.value) === String(value));
    if (option) return String(option.label);
    if (value === true) return "Yes";
    if (value === false) return "No";
    return value === null || value === undefined || value === "" ? "—" : String(value);
};

/** The settings the form moved away from what the server holds. */
export function configChanges(fields: FieldDataType[], defaults: Record<string, unknown>, values: Record<string, unknown>): DraftChange[] {
    return Object.keys(defaults)
        .filter(key => String(values[key] ?? "") !== String(defaults[key] ?? ""))
        .map(key => {
            const field = fields.find(item => item.field_name === key);
            return { kind: "changed" as const, label: field?.label ?? key, before: shown(field, defaults[key]), after: shown(field, values[key]) };
        });
}

export function listViewChanges(page: FeedingPage, draft: ListViewDraft, definitions: Filter[], savedLogic: string): DraftChange[] {
    const changes: DraftChange[] = [];
    const current = page.source.list_view;
    if (draft.source) changes.push({ kind: "changed", label: `Source replaced by “${draft.source.name}”`, before: current?.name });
    if (draft.filters && current && (draft.filters.filter_tree || current.filter_tree)) {
        // Advanced filters: one change, the trees summed up.
        const text = (tree: FilterTree | null | undefined, drq: DRQ) =>
            tree
                ? describeTree(tree)
                      .map(chip => [chip.label, chip.value].filter(Boolean).join(" "))
                      .join(" · ")
                : `${toInstances(drq).length} filter(s)`;
        changes.push({ kind: "changed", label: "Source filters", before: text(current.filter_tree, current.filters), after: text(draft.filters.filter_tree, draft.filters.filters) });
    } else if (draft.filters && current) {
        diffFilters(toInstances(current.filters), toInstances(draft.filters.filters), definitions, savedLogic, draft.filters.filter_logic ?? "").forEach(change =>
            changes.push({ ...change, label: "Source filters" })
        );
    }
    page.exclusions.filter(view => draft.removed.includes(view.link_id)).forEach(view => changes.push({ kind: "removed", label: `Exclusion “${view.name}” removed` }));
    draft.added.forEach(view => changes.push({ kind: "added", label: `Exclusion “${view.name}” added` }));
    return changes;
}

/**
 * The `config` of a step's Save: the settings it edits. The first write creates the settings, so it also
 * carries the object and the cap (`objectType` overrides the served one, e.g. a source on another object).
 */
export function configBody(page: FeedingPage, values: Record<string, unknown>, objectType?: string): Record<string, unknown> {
    if (page.config.exists) return values;
    return { cap_per_pass: page.config.values.cap_per_pass, ...values, object_type: objectType ?? page.source.object_type };
}

/** The body of the Save (and of its impact): only what the draft changes. */
export function draftBody(config: Record<string, unknown> | null, draft: ListViewDraft): Record<string, unknown> {
    return {
        ...(config ? { config } : {}),
        ...(draft.source ? { source: draft.source.spec } : {}),
        ...(draft.filters ? { source_filters: draft.filters } : {}),
        ...(draft.added.length ? { exclusions_add: draft.added.map(view => view.spec) } : {}),
        ...(draft.removed.length ? { exclusions_remove: draft.removed } : {})
    };
}
